import React, { useState } from "react"
import { Redirect, Route, Router, Switch, withRouter } from "react-router-dom"
import Home from "./core/Home"
import { connect } from "react-redux"
import SignupContainer from "./components/user/SignupContainer"
import history from "./history"
import SignInContainer from "./components/user/SignInContainer"
import { signOut } from "./components/user/redux/reducers"
import SideNav, { NavItem, NavIcon, NavText } from "@trendmicro/react-sidenav"
import Profile from "./components/user/Profile"
import get from "lodash.get"
import Users from "./components/user/Users"
import DeleteUser from "./components/user/DeleteUser"
import EditProfile from "./components/user/EditProfile"
import PrivateRoute from "./PrivateRoute"
import PostInfo from "./components/post/PostInfo"
import EditPost from "./components/post/EditPost"
import CellsList from "./components/cell/CellsList"
import CellDetail from "./components/cell/CellDetail"
import CreateCell from "./components/cell/CreateCell"
import TheGrid from "./components/grid/TheGrid"

const PATH = process.env.NODE_ENV === "production" ? "/" : "/"

// Keep the sidenav highlight in sync with the route, so navigation that
// doesn't go through the sidenav (Browse Cells, cell chips, redirects)
// still lights up the right icon.
const navKeyForPath = pathname => {
  if (pathname.startsWith("/grid")) return "grid"
  if (
    pathname.startsWith("/cells") ||
    pathname.startsWith("/cell/") ||
    pathname.startsWith("/createCell") ||
    pathname.startsWith("/post/") ||
    pathname.startsWith("/editPost/")
  ) {
    return "cells"
  }
  if (pathname.startsWith("/users")) return "users"
  if (pathname.startsWith("/user/") || pathname.startsWith("/editUser/")) {
    return "profile"
  }
  if (pathname.startsWith("/signIn")) return "signIn"
  if (pathname.startsWith("/signUp")) return "signUp"
  if (pathname === "/") return "home"
  return null
}

const renderLogout = (user, signOut) => {
  if (!user.loaded.token) {
    return
  }

  return (
    <NavItem eventKey="signOut" onClick={signOut}>
      <NavIcon>
        <i
          className="icon-color fas fa-sign-out-alt"
          style={{ fontSize: "1.50em" }}
        />
      </NavIcon>
      <NavText>
        <div className="link-color">Logout</div>
      </NavText>
    </NavItem>
  )
}

const renderProfile = user => {
  if (!user.loaded.token) {
    return
  }

  return (
    <NavItem eventKey={`profile`}>
      <NavIcon>
        <i className="icon-color fas fa-user" style={{ fontSize: "1.50em" }} />
      </NavIcon>
      <NavText>
        <div className="link-color">Profile</div>
      </NavText>
    </NavItem>
  )
}

const renderUsers = user => {
  if (!user.loaded.token) {
    return
  }

  return (
    <NavItem eventKey="users">
      <NavIcon>
        <i className="icon-color fa fa-users" style={{ fontSize: "1.50em" }} />
      </NavIcon>
      <NavText>
        <div className="link-color">Users</div>
      </NavText>
    </NavItem>
  )
}

const renderGrid = user => {
  if (!user.loaded.token) {
    return
  }

  return (
    <NavItem eventKey="grid">
      <NavIcon>
        <i className="icon-color fas fa-th" style={{ fontSize: "1.50em" }} />
      </NavIcon>
      <NavText>
        <div className="link-color">The Grid</div>
      </NavText>
    </NavItem>
  )
}

const renderCells = user => {
  if (!user.loaded.token) {
    return
  }

  return (
    <NavItem eventKey="cells">
      <NavIcon>
        <i
          className="icon-color fas fa-th-large"
          style={{ fontSize: "1.50em" }}
        />
      </NavIcon>
      <NavText>
        <div className="link-color">Cells</div>
      </NavText>
    </NavItem>
  )
}

const MainRouter = ({ signOut, user }) => {
  function clickedTest() {
    setExpanded(!expanded)
  }

  const getUserId = get(user, "loaded.user._id")
  const isLoggedIn = user.loaded.token
  const [expanded, setExpanded] = useState(false)
  return (
    <div>
      <Router history={history}>
        <Route
          render={({ location, history }) => (
            <React.Fragment>
              <SideNav
                onToggle={() => clickedTest()}
                componentClass="div"
                className="sidebar-nav"
                onSelect={selected => {
                  const to = "/" + selected
                  if (selected === "profile") {
                    return history.push(`/user/${getUserId}`)
                  }
                  if (location.pathname !== to) {
                    history.push(to)
                  }
                }}
              >
                <SideNav.Toggle />
                <SideNav.Nav selected={navKeyForPath(location.pathname)}>
                  {!isLoggedIn && (
                    <NavItem eventKey="home">
                      <NavIcon>
                        <i
                          className="icon-color fa fa-fw fa-home"
                          style={{ fontSize: "1.50em" }}
                        />
                      </NavIcon>
                      <NavText>
                        <div className="link-color">Home</div>
                      </NavText>
                    </NavItem>
                  )}
                  {!isLoggedIn && (
                    <NavItem eventKey="signIn">
                      <NavIcon>
                        <i
                          className="icon-color fas fa-sign-in-alt"
                          style={{ fontSize: "1.50em" }}
                        />
                      </NavIcon>
                      <NavText>
                        <div className="link-color">Login</div>
                      </NavText>
                    </NavItem>
                  )}
                  {!isLoggedIn && (
                    <NavItem eventKey="signUp">
                      <NavIcon>
                        <i
                          className="icon-color fas fa-user-plus"
                          style={{ fontSize: "1.50em" }}
                        />
                      </NavIcon>
                      <NavText>
                        <div className="link-color">Sign Up</div>
                      </NavText>
                    </NavItem>
                  )}
                  {renderGrid(user)}
                  {renderCells(user)}
                  {renderProfile(user)}
                  {renderUsers(user)}
                  {renderLogout(user, signOut)}
                </SideNav.Nav>
              </SideNav>

              <Switch>
                <Route
                  path={`${PATH}signUp`}
                  render={props => (
                    <SignupContainer isExpanded={expanded} {...props} />
                  )}
                />
                <Route
                  path={`${PATH}signIn`}
                  render={props => (
                    <SignInContainer isExpanded={expanded} {...props} />
                  )}
                />
                <Route
                  path={`${PATH}homepage`}
                  render={() => <Redirect to="/grid" />}
                />
                <PrivateRoute
                  path={`${PATH}grid`}
                  component={TheGrid}
                  isExpanded={expanded}
                />
                <Route
                  path={`${PATH}user/:userId`}
                  render={props => <Profile isExpanded={expanded} {...props} />}
                />
                <Route
                  path={`${PATH}users`}
                  render={props => <Users isExpanded={expanded} {...props} />}
                />
                <Route
                  path={`${PATH}deleteUser/:userId`}
                  render={props => (
                    <DeleteUser isExpanded={expanded} {...props} />
                  )}
                />
                <Route
                  path={`${PATH}post/:postId`}
                  render={props => (
                    <PostInfo isExpanded={expanded} {...props} />
                  )}
                />
                <Route
                  path={`${PATH}editPost/:postId`}
                  render={props => (
                    <EditPost isExpanded={expanded} {...props} />
                  )}
                />
                <Route
                  path={`${PATH}createCell`}
                  render={props => (
                    <CreateCell isExpanded={expanded} {...props} />
                  )}
                />
                <Route
                  path={`${PATH}cells`}
                  render={props => (
                    <CellsList isExpanded={expanded} {...props} />
                  )}
                />
                <Route
                  path={`${PATH}cell/:cellId`}
                  render={props => (
                    <CellDetail isExpanded={expanded} {...props} />
                  )}
                />
                <PrivateRoute
                  path={`${PATH}editUser/:userId`}
                  component={EditProfile}
                  isExpanded={expanded}
                />
                <Route
                  path={`${PATH}`}
                  render={props => <Home isExpanded={expanded} {...props} />}
                />
              </Switch>
            </React.Fragment>
          )}
        />
      </Router>
    </div>
  )
}

const mapState = state => {
  return {
    user: state.user
  }
}

export default withRouter(
  connect(
    mapState,
    { signOut }
  )(MainRouter)
)
